/**
 * v2 Settings → Audit log. Read-only paginated list.
 *
 * Every operator-initiated write hits ``audit_log`` via
 * ``chassis_shared.write_audit``. The recent additions
 * (ALARM_ACK / ALARM_MUTE / ALARM_RULE_CHANGE) surface here too.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { HelpButton } from "../../help/HelpPanel";

type AuditRow = {
  id: number;
  ts: string;
  action: string;
  actor: string;
  outcome: string;
  chassis_id: number | null;
  target: string | null;
  detail: Record<string, unknown> | null;
  request_id: string | null;
};

// Backend is keyset-paginated (apps/web/app/routers/audit.py): it accepts
// ``cursor`` + ``limit`` and returns ``next_cursor`` (no total count, no
// offset). We track a stack of the cursors used to fetch each page so Prev
// walks back and Next walks forward.
type AuditResponse = { rows: AuditRow[]; next_cursor: string | null };

const OUTCOME_TONE: Record<string, BadgeTone> = {
  ok: "success",
  error: "danger",
  denied: "warning",
};

export function Audit() {
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  // Cursor stack: one entry per page visited. The first page uses a null
  // cursor; the current page's cursor is the last entry. Changing a filter
  // resets the stack to the first page.
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const PAGE_SIZE = 50;
  const currentCursor = cursors[cursors.length - 1];
  const pageNum = cursors.length;
  const resetPaging = () => setCursors([null]);

  const q = useQuery<AuditResponse>({
    queryKey: ["v2", "audit", actionFilter, actorFilter, currentCursor],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (currentCursor) params.set("cursor", currentCursor);
      if (actionFilter.trim()) params.set("action", actionFilter.trim());
      if (actorFilter.trim()) params.set("actor", actorFilter.trim());
      const res = await api.get<AuditResponse>(`/audit?${params}`);
      return res.data;
    },
  });

  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Audit log</h1>
          <HelpButton topic="settings-audit" />
        </div>
        <p className="text-xs text-slate-500">
          Every operator-initiated write — login, provisioning,
          template edits, alarm ack/mute, schedule changes — with
          actor + outcome + request_id.
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-900 shadow-border p-3 text-xs">
        <input
          type="text"
          placeholder="action (e.g. alarm_ack)"
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            resetPaging();
          }}
          className="rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-slate-100 placeholder:text-slate-500"
        />
        <input
          type="text"
          placeholder="actor (username)"
          value={actorFilter}
          onChange={(e) => {
            setActorFilter(e.target.value);
            resetPaging();
          }}
          className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-100 placeholder:text-slate-500"
        />
        <span className="ml-auto tabular-nums text-slate-500">
          {(q.data?.rows.length ?? 0).toLocaleString()} rows · page {pageNum}
        </span>
        <button
          type="button"
          disabled={cursors.length <= 1}
          onClick={() =>
            setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c))
          }
          className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        >
          ←
        </button>
        <button
          type="button"
          disabled={!q.data?.next_cursor}
          onClick={() => {
            const next = q.data?.next_cursor;
            if (next) setCursors((c) => [...c, next]);
          }}
          className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        >
          →
        </button>
      </div>
      <div className="rounded-lg bg-slate-900 shadow-border p-3">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : q.isError ? (
          <p className="text-xs text-red-400">
            Failed to load audit log. Try refreshing or narrowing the filters.
          </p>
        ) : (q.data?.rows.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-500">No audit rows match.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">When</th>
                <th className="px-2 py-1">Actor</th>
                <th className="px-2 py-1">Action</th>
                <th className="px-2 py-1">Outcome</th>
                <th className="px-2 py-1">Chassis</th>
                <th className="px-2 py-1">Target</th>
                <th className="px-2 py-1">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {(q.data?.rows ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={r.ts} />
                  </td>
                  <td className="px-2 py-1">{r.actor}</td>
                  <td className="px-2 py-1 font-mono">{r.action}</td>
                  <td className="px-2 py-1">
                    <Badge tone={OUTCOME_TONE[r.outcome] ?? "neutral"}>
                      {r.outcome}
                    </Badge>
                  </td>
                  <td className="px-2 py-1">
                    {r.chassis_id ? (
                      <Link
                        to={`/chassis/${r.chassis_id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        chassis-{r.chassis_id}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-2 py-1 font-mono text-[11px]">
                    {r.target ? <CopyableCell value={r.target} /> : "—"}
                  </td>
                  <td className="px-2 py-1 text-[10px]">
                    {r.detail ? (
                      <details>
                        <summary className="cursor-pointer text-slate-400">
                          json
                        </summary>
                        <pre className="mt-1 overflow-x-auto rounded bg-slate-950 p-1.5 font-mono text-[10px] text-slate-400">
                          {JSON.stringify(r.detail, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
